/** @param {string | null | undefined} raw */
export function stripLocationPrefix(raw) {
  if (raw == null || typeof raw !== 'string') return '';
  let t = raw.replace(/\s+/g, ' ').trim();
  t = t.replace(/^\[Location\]\s*[-–:]\s*/i, '');
  t = t.replace(/^\[Location\]\s*/i, '');
  t = t.replace(/^\(Location\)\s*[-–:]\s*/i, '');
  t = t.replace(/^\(Location\)\s*/i, '');
  t = t.replace(/^Location\s*[-–:]\s*/i, '');
  t = t.replace(/^Location\s+/i, '');
  t = t.replace(/^\[?\s*location\s*]?\s*[-–:]\s*/i, '');
  return t.trim();
}

/** Titres courts pour affichage et catalogue (lisibilité / formulations génériques). */
export function simplifyFormationTitle(raw) {
  let t = stripLocationPrefix(raw);
  if (!t) return '';

  t = t.replace(/\.$/, '');

  t = t.replace(/\s*[-–:]\s*Audit Clinique\s*$/i, '');
  t = t.replace(
    /\s*:\s*audit et (?:amélioration des|bonnes) pratiques (?:professionnelles|infirmières)\s*$/i,
    '',
  );
  t = t.replace(/\s*:\s*Qualité et sécurité des pratiques illustrées par des cas d'usage\s*$/i, '');
  t = t.replace(/\s*:\s*Rôle des médecins et des sages femmes dans la prescription des vaccins\s*$/i, '');
  t = t.replace(/\s*:\s*Rôle de l'infirmier\s*$/i, '');
  t = t.replace(/\s*:\s*Rôle de l'infirmière\s*$/i, '');

  const anywhereContext = [
    /\s+en médecine générale\b/gi,
    /\s+en médecine énérale\b/gi,
    /\s+en cabinet de médecine générale\b/gi,
  ];
  for (const re of anywhereContext) {
    t = t.replace(re, '');
  }

  const trailingContext = [
    /\s+en cabinet de ville\s*$/i,
    /\s+par le médecin généraliste\s*$/i,
    /\s+par les médecins généralistes\s*$/i,
    /\s+dans la raisonnement clinique en médecine générale\s*$/i,
    /\s+dans le raisonnement clinique en médecine générale\s*$/i,
    /\s+et amélioration de la couverture vaccinale en France\s*$/i,
    /\s+en kinésithérapie du patient âgé de 18 à 40 ans\s*$/i,
    /\s+dans la prise en charge médicale et chirurgicale du strabisme chez l'enfant\s*$/i,
    /\s+dans la prescription des vaccins\s*$/i,
    /\s+illustrées par des cas d'usage\s*$/i,
  ];
  for (const re of trailingContext) {
    t = t.replace(re, '');
  }

  t = t.replace(/\s*[-–]\s*niveau\s+[12]\s*$/i, '');
  t = t.replace(/\s*\(Part\s+\d+\)\s*$/i, '');

  t = t.replace(/\s+du patient\b/gi, '');
  t = t.replace(/\s+de l'enfant\b/gi, ' (enfant)');
  t = t.replace(/\s+de l’enfant\b/gi, ' (enfant)');
  t = t.replace(/\s+chez l'enfant\b/gi, ' (enfant)');
  t = t.replace(/\s+chez l’enfant\b/gi, ' (enfant)');

  const abbrevs = [
    ['infections sexuellement transmissibles', 'IST'],
    ['symptômes évocateurs d\'IST', 'symptômes IST'],
    ['Lecture et interprétation clinique de l\'électrocardiogramme', 'Lecture ECG'],
    ['interprétation clinique de l\'électrocardiogramme', 'interprétation ECG'],
    ['électrocardiogramme', 'ECG'],
    ['Dépistage et prise en charge', 'Dépistage & prise en charge'],
    ['dépistage et prise en charge', 'dépistage & prise en charge'],
    ['Repérage et prise en charge', 'Repérage & prise en charge'],
    ['repérage et prise en charge', 'repérage & prise en charge'],
    ['Démarche diagnostique et raisonnement clinique', 'Démarche diagnostique'],
    ['Démarche diagnostique devant', 'Diagnostic devant'],
    ['Prévention des infections liées aux voies veineuses périphériques', 'Prévention IVP'],
    ['Modalités de collaboration entre ophtalmologistes et orthoptistes', 'Collaboration ophtalmo–orthoptie'],
    ['maîtriser l\'indication, l\'analyse et la réglementation du Cone Beam', 'indication & analyse Cone Beam'],
    ['Intégration des innovations dans la pratique médicale', 'Innovations en pratique médicale'],
    ['Promotion de la vaccination en médecine générale', 'Promotion vaccination'],
    ['Points d\'appels en cabinet de ville, accompagnement du parcours ambulatoire et suivi à long terme', 'Repères en cabinet'],
    ['Du repérage prénatal des vulnérabilités parentales à l\'individualisation de l\'accompagnement postnatal', 'Vulnérabilités parentales & suivi postnatal'],
    ['Gestion des conflits : Clé de lecture pour les relations professionnelles', 'Gestion des conflits'],
    ['Clé de lecture pour les relations professionnelles', 'Relations professionnelles'],
    [': Rôle de l\'infirmier', ''],
    [': Rôle de l\'infirmière', ''],
    ['Télémédecine : Qualité et sécurité des pratiques', 'Télémédecine — qualité & sécurité'],
  ];
  for (const [from, to] of abbrevs) {
    t = t.replace(new RegExp(from.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), to);
  }

  const colon = t.match(/^([^:]{4,80}):\s*(.+)$/);
  if (colon) {
    const head = colon[1].trim();
    let tail = colon[2].trim();
    if (tail.length > 48) {
      const cut = tail.match(/^[^,;]+/);
      tail = cut ? cut[0].trim() : tail.slice(0, 48).trim();
    }
    if (tail.length <= 48 && head.length + tail.length + 3 <= 72) {
      t = `${head} — ${tail}`;
    } else if (head.length <= 72) {
      t = head;
    }
  }

  const dash = t.match(/^([^–-]{3,60})\s*[-–]\s*(.+)$/);
  if (dash) {
    const head = dash[1].trim();
    let tail = dash[2].trim();
    if (tail.length > 42) {
      const cut = tail.match(/^[^,;]+/);
      tail = cut ? cut[0].trim() : tail.slice(0, 42).trim();
    }
    t = `${head} — ${tail}`;
  }

  t = t.replace(/\s+/g, ' ').replace(/\s*[-–—]\s*[-–—]\s*/g, ' — ').trim();
  t = t.replace(/\s*,\s*$/, '');
  t = t.replace(/\.$/, '');

  const MAX = 78;
  if (t.length > MAX) {
    const slice = t.slice(0, MAX);
    const lastSpace = slice.lastIndexOf(' ');
    t = (lastSpace > 40 ? slice.slice(0, lastSpace) : slice).trim();
  }

  if (t.length > 0) {
    t = t.charAt(0).toUpperCase() + t.slice(1);
  }

  return t;
}
